import { useEffect, useRef, type ReactNode } from 'react';
import {
  flexRender,
  type Row,
  type Table,
  type RowData,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    numeric?: boolean;
    flex?: boolean;
  }
}

interface DsTableProps<T> {
  table: Table<T>;
  selectedRowId?: string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  cellClassName?: (rowIndex: number, colId: string) => string | undefined;
  onCellClick?: (row: T, rowIndex: number, colId: string) => void;
  emptyMessage?: ReactNode;
  className?: string;
  fixedLayout?: boolean;
  enableColumnResizing?: boolean;
  rowHeight?: number;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  scrollToIndex?: number;
}

const ROW_HEIGHT = 29;

export function DsTable<T>({
  table,
  selectedRowId,
  onRowClick,
  rowClassName,
  cellClassName,
  onCellClick,
  emptyMessage = 'Keine Daten.',
  className,
  fixedLayout,
  enableColumnResizing,
  rowHeight = ROW_HEIGHT,
  containerRef: externalContainerRef,
  scrollToIndex,
}: DsTableProps<T>) {
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = externalContainerRef ?? internalRef;
  const rows = table.getRowModel().rows;
  const allColumns = table.getAllLeafColumns();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => containerRef.current,
    overscan: 20,
  });

  useEffect(() => {
    if (scrollToIndex != null && scrollToIndex >= 0 && scrollToIndex < rows.length) {
      const el = containerRef.current?.querySelector(`[data-index="${scrollToIndex}"]`) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
      setTimeout(() => {
      }, 0);
    }
  }, [scrollToIndex]);

  const colWidths = fixedLayout
    ? allColumns.map((col) => {
        if (col.columnDef.meta?.flex) return undefined;
        const def = col.columnDef;
        return (def.maxSize != null && def.maxSize < 10000) ? def.maxSize : col.getSize();
      })
    : null;

  return (
    <div
      ref={externalContainerRef ? undefined : internalRef}
      className={`ds-scroll${className ? ` ${className}` : ''}`}
      style={{ overflow: 'auto', position: 'relative', flex: 1, minHeight: 0 }}
    >
      <table
        className="ds-table"
        style={{ display: 'grid', tableLayout: fixedLayout ? 'fixed' : undefined }}
      >
        {fixedLayout && colWidths && (
          <colgroup>
            {colWidths.map((w, i) =>
              w != null ? <col key={allColumns[i].id} style={{ width: w }} /> : <col key={allColumns[i].id} />,
            )}
          </colgroup>
        )}
        <thead style={{ display: 'grid', position: 'sticky', top: 0, zIndex: 2 }}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ display: 'flex', width: '100%' }}>
              {headerGroup.headers.map((header) => {
                const isNumeric = header.column.columnDef.meta?.numeric;
                const isFlex = header.column.columnDef.meta?.flex;
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const canResize = enableColumnResizing && header.column.getCanResize();
                return (
                  <th
                    key={header.id}
                    data-col-id={header.column.id}
                    className={`${isNumeric ? 'num ' : ''}${canSort ? 'sortable' : ''}${sorted ? ' sorted' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      width: isFlex ? undefined : header.getSize(),
                      flex: isFlex ? 1 : undefined,
                      flexShrink: isFlex || !enableColumnResizing ? undefined : 0,
                      cursor: canSort ? 'pointer' : undefined,
                    }}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {canSort && (
                      <span style={{
                        display: 'inline-block',
                        marginLeft: 4,
                        color: sorted ? 'var(--accent)' : 'var(--ink-4)',
                        fontSize: 9,
                      }}>
                        {sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '◇'}
                      </span>
                    )}
                    {canResize && (
                      <span
                        className={`ds-col-resizer${header.column.getIsResizing() ? ' resizing' : ''}`}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          style={{
            display: 'grid',
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
          }}
        >
          {rows.length === 0 ? (
            <tr style={{ display: 'flex' }}>
              <td
                colSpan={allColumns.length}
                style={{ textAlign: 'center', padding: 30, color: 'var(--ink-3)', flex: 1 }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<T>;
              const id = row.id;
              const extra = rowClassName?.(row.original) ?? '';
              const selected = selectedRowId != null && id === selectedRowId;
              return (
                <tr
                  key={id}
                  data-index={virtualRow.index}
                  ref={(node) => rowVirtualizer.measureElement(node)}
                  className={`${selected ? 'selected' : ''} ${extra}`.trim() || undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  style={{
                    display: 'flex',
                    position: 'absolute',
                    transform: `translateY(${virtualRow.start}px)`,
                    width: '100%',
                    height: rowHeight,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isNumeric = cell.column.columnDef.meta?.numeric;
                    const isFlex = cell.column.columnDef.meta?.flex;
                    const extraCellCls = cellClassName?.(virtualRow.index, cell.column.id);
                    return (
                      <td
                        key={cell.id}
                        data-col-id={cell.column.id}
                        className={[isNumeric && 'num', extraCellCls].filter(Boolean).join(' ') || undefined}
                        onClick={onCellClick ? () => onCellClick(row.original, virtualRow.index, cell.column.id) : undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: isFlex ? undefined : cell.column.getSize(),
                          flex: isFlex ? 1 : undefined,
                          flexShrink: isFlex || !enableColumnResizing ? undefined : 0,
                          overflow: 'hidden',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export type { SortingState } from '@tanstack/react-table';
